/**
 * Per-line Sage comments — read-only sub-block rendered inside each
 * line item row in OrderDetailModal.
 *
 * Two sources are surfaced:
 *  - Sage `CommentText` on the matching SO/invoice line (indigo).
 *  - `/`-prefixed memo rows attached to the line (violet).
 *
 * The internal `OrderLineItem.notes` field (amber LLM/portal note) is
 * still rendered by OrderDetailModal directly — this component handles
 * only the Sage-sourced annotations.
 *
 * Designed to be a no-op when no Sage data is available, so it's safe
 * to mount under every line regardless of order state.
 *
 * (The old "+ Add line comment — coming soon" affordance was removed in
 * the 2026-07 review-noise pass. The write side now lives in
 * OrderDetailModal directly: a draft line's teal "+ Sage comment" editor
 * PATCHes `sage_comment_text`, which is sent to Sage's native CommentText
 * on submission — see LINE_PAYLOAD_FIELD_SOURCES in services/orders/intake
 * and SageOrderLinePayload in services/orders/sage_api.py.)
 */

import type { LineCommentBundle } from '../../services/portalOrders';
import styles from './LineItemSageComments.module.css';

interface Props {
  /** Resolved bundle (or null if no Sage data for this line). */
  bundle: LineCommentBundle | null;
}

const LineItemSageComments: React.FC<Props> = ({ bundle }) => {
  const sageText = bundle?.sage_comment_text?.trim();
  const memos = bundle?.memo_comments ?? [];
  const hasSageContent = Boolean(sageText) || memos.length > 0;

  if (!hasSageContent) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      {sageText && (
        <div className={`${styles.line} ${styles.lineSage}`}>
          <span className={styles.icon} aria-hidden>◆</span>
          <span className={styles.body}>
            <span className={styles.label}>Sage comment</span>
            <span>{sageText}</span>
          </span>
        </div>
      )}

      {memos.map((m, i) => (
        <div
          key={`${m.code}-${i}`}
          className={`${styles.line} ${styles.lineMemo}`}
        >
          <span className={styles.icon} aria-hidden>∥</span>
          <span className={styles.body}>
            <span className={styles.label}>Sage memo</span>
            <code className={styles.memoCode}>{m.code}</code>
            <span>{m.text}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

export default LineItemSageComments;
